import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { HashLoader } from "react-spinners";
import { ErrorMessage, Field, Formik } from "formik";
import * as yup from "yup";
import useVerifyBVN from "../../hooks/useVerifyBVN";
import SignUpNavbar from "../../components/SignUpNavbar";
import { useAuth } from "../../context/AuthContext";

const schema = yup.object().shape({
  bvn: yup
    .string()
    .required("BVN is required")
    .matches(/^\d{11}$/, "Must be exactly 11 digits"),
});

const BVNVerification = () => {
  const { user, refetch } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });

    if (user?.compliance?.bvn_verified) {
      navigate("/sign-up/complete-profile", { replace: true });
    }
  }, [user, navigate]);

  const { isVerifyingBVNError, verifyBvn, verifyingBVN, verifyingBVNError } =
    useVerifyBVN();

  const isLoading = useMemo(() => verifyingBVN, [verifyingBVN]);

  const isError = useMemo(() => isVerifyingBVNError, [isVerifyingBVNError]);

  const error = useMemo(() => verifyingBVNError, [verifyingBVNError]);

  const [search] = useSearchParams();

  const trxRef = search.get("trxref");

  return (
    <div>
      {isLoading && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}

      <Formik
        initialValues={{
          bvn: "",
        }}
        validationSchema={schema}
        onSubmit={(values, { resetForm }) => {
          verifyBvn(
            {
              ...values,
              payment_reference: trxRef,
            },
            {
              async onSuccess(data) {
                // Refresh the cached profile so `compliance.bvn_verified` is up
                // to date before the onboarding gate re-evaluates — otherwise a
                // successful verification is bounced straight back to this page.
                await refetch();
                navigate("/sign-up/complete-profile", {
                  replace: true,
                  state: {
                    nin_data: data.data,
                  },
                });
                resetForm();
              },
            }
          );
        }}
      >
        {({ handleSubmit, isValid, dirty }) => {
          return (
            <>
              <SignUpNavbar />
              <div className="flex flex-col justify-center items-center p-20 px-5 md:px-20 gap-8 text-lg">
                <div className="text-4xl md:text-3xl lg:text-5xl max-w-3xl font-bold text-center">
                  BVN Verification
                </div>
                <form
                  onSubmit={handleSubmit}
                  className="w-full flex-col gap-5 flex items-center justify-center text-base"
                >
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label htmlFor="bvn" className="text-sm">
                      BVN*
                    </label>
                    <Field
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={11}
                      placeholder="Enter your BVN"
                      name="bvn"
                      id="bvn"
                      className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                    />
                    <ErrorMessage
                      name="bvn"
                      component="div"
                      className="text-red-500"
                    />
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    {isError && (
                      <div className="text-red-500 text-left flex">
                        {error?.message}
                      </div>
                    )}
                    <button
                      disabled={!isValid || !dirty}
                      type="submit"
                      className="p-4 px-5 w-full border-visaro-gray-700 border rounded-lg font-bold bg-deepBlue text-white text-sm"
                    >
                      Confirm
                    </button>
                  </div>
                  {/* <div className="flex flex-col gap-2 max-w-md w-full">
                      <Link to="/login" className="text-center text-gray-600">
                        Already have an account?{" "}
                        <span className="text-visaro-orange">Log in</span>
                      </Link>
                    </div> */}
                </form>
              </div>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default BVNVerification;
